import React, { FC, useEffect, useState } from 'react';
import { BasicContainer, FlexRow } from '@minecraft.atoms';
import { CarouselImage } from './ImageCarousel';

export type ImageCarouselFillProps = {
  images: CarouselImage[];
  /** Auto-advance every three seconds. */
  autoplay?: boolean;
  'data-testid'?: string;
  'data-qa-id'?: string;
};

/**
 * An image gallery that fills its parent's height and crops each image to cover it,
 * with position indicators and optional autoplay. Use `ImageCarousel` for a
 * fixed-size, rounded gallery with arrows.
 */
export const ImageCarouselFill: FC<ImageCarouselFillProps> = ({
  images,
  autoplay = false,
  'data-testid': testId = 'image-carousel-fill',
  'data-qa-id': qaId,
}) => {
  const [selectedSlide, setSelectedSlide] = useState(0);
  const imageCount = images.length;
  const activeSlide = imageCount ? selectedSlide % imageCount : 0;

  useEffect(() => {
    if (!autoplay || imageCount < 2) {
      return undefined;
    }

    const timer = setInterval(() => {
      setSelectedSlide((index) => (index + 1) % imageCount);
    }, 3000);

    return () => clearInterval(timer);
  }, [autoplay, imageCount]);

  if (!imageCount) {
    return null;
  }

  return (
    <BasicContainer className="cn_relative cn_overflow-hidden" w="full" h="full" data-testid={testId} data-qa-id={qaId}>
      {images.map((image, index) => (
        <BasicContainer key={`${image.id ?? index}:${image.src}`} w="full" h="full" hidden={index !== activeSlide}>
          <img src={image.src} alt={image.alt} className="cn_block cn_w-full cn_h-full cn_object-cover" />
        </BasicContainer>
      ))}
      {imageCount > 1 && (
        <FlexRow
          className="cn_absolute cn_bottom-0"
          w="full"
          justifyContent="center"
          alignItems="center"
          gap="0"
          wrap="nowrap"
        >
          {images.map((image, index) => (
            <BasicContainer key={image.id ?? image.src} className="cn_relative cn_w-nav-badge cn_h-6">
              <BasicContainer
                className="cn_center-absolute cn_pointer-events-none"
                minWidth={0.4}
                minHeight={0.4}
                borderRadius="full"
                bgColor={activeSlide === index ? 'card' : 'carouselDotInactive'}
                data-testid={`${testId}-dot-${index + 1}`}
                data-active={activeSlide === index ? 'true' : 'false'}
              />
            </BasicContainer>
          ))}
        </FlexRow>
      )}
    </BasicContainer>
  );
};

export default ImageCarouselFill;
